<template>
    <v-form ref="form">
        <v-row no-gutters class="pb-2">
            <v-col cols="12" md="3" class="pe-1">
                <v-text-field
                    label="Code" 
                    :value="form.code"
                    hide-details="auto"
                    dense 
                    background-color="disabled"
                    disabled
                    outlined
                    >
                </v-text-field>
            </v-col>
            <v-spacer></v-spacer>
            <v-col cols="12" md="3" v-if="form.requisition_id && form.requisition_code">
                <v-text-field
                    label="Requisition Code"
                    v-model="form.requisition_code"
                    dense
                    outlined
                    background-color="disabled"
                    disabled
                    hide-details="auto"
                >
                </v-text-field>
            </v-col>
        </v-row>
        <v-row no-gutters class="pb-2">
            <v-col cols="12" md="3" class="pe-1">
                <PDatePicker
                    label="Date"
                    v-model="form.date"
                    :rules="[isRequired]"
                    hide
                    dense 
                    outlined>
                </PDatePicker>
                
            </v-col>
            <v-col cols="12" md="3" class="pe-1">
                <v-autocomplete
                    label="Warehouse"
                    v-model="form.warehouse_id"
                    :items="warehouses"
                    item-text="display_name"
                    item-value="id"
                    :rules="[isRequired]"
                    @update:search-input="searchWarehouses"
                    outlined
                    dense
                    hide-details="auto"
                >
                </v-autocomplete>
            </v-col>
            <v-col cols="12" md="3" class="pe-1">
                <v-text-field
                    label="Receving Point"
                    v-model="form.receiving_point"
                    :rules="[isRequired]"
                    dense
                    outlined
                    hide-details="auto"
                >
                </v-text-field>
            </v-col>
        </v-row>
        <v-row no-gutters class="pb-2">
            <v-col cols="12" md="6" class="pe-1">
                <v-autocomplete
                    label="Supplier"
                    :items="suppliers"
                    @update:search-input="searchSuppliers"
                    item-text="display_name"
                    :rules="[isRequired]"
                    item-value="id"
                    v-model="form.supplier_id"
                    hide-no-data
                    dense
                    outlined
                    hide-details="auto"
                    clearable
                >
                </v-autocomplete>
            </v-col>
            <v-col cols="12" md="3" class="pe-1">
                <v-autocomplete
                    label="Currency"
                    v-model="form.currency_id"
                    :items="currencies"
                    :rules="[isRequired]"
                    item-text="display_name"
                    item-value="id"
                    @update:search-input="searchCurrency"
                    dense
                    outlined
                    hide-details="auto"
                >
                </v-autocomplete>
            </v-col>
        </v-row>
        <v-divider class="mb-2"></v-divider>

        <PurchaseOrderFormLine
            v-for="(line, index) in form.lines"
            :key="line.key"
            v-model="form.lines[index]"
            :currency="currency"          
            :index="index"
            @delete:line="handleDeleteLine"
            />
        
        <v-btn block depressed small @click="addLine">
            <v-icon left>mdi-playlist-plus</v-icon>
            add line
        </v-btn>
        <v-divider class="my-2"></v-divider>
        <v-row no-gutters>
            <v-spacer></v-spacer>
            <v-col cols="12" md="4">
                <v-sheet outlined>
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <th >Subtotal</th>
                                <td>{{currency ? currency.abbreviation : null}} {{formatNumber(getTotal(form.lines, "subtotal"))}}</td>
                            </tr>
                            <tr>
                                <th >Tax</th>
                                <td>{{currency ? currency.abbreviation : null}} {{formatNumber(getTotal(form.lines, "tax"))}}</td>

                            </tr>
                            <tr class="font-weight-bold">
                                <th >Total</th>
                                <td>{{currency ? currency.abbreviation : null}} {{formatNumber(getTotal(form.lines, "total"))}} @ {{exchangeRate}}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-sheet>
            </v-col>
        </v-row>
        <ExchangeRateIndex
        :value="exchangeRateIndexShown"
        @input="$emit('update:exchange-rate-index-shown', $event)"
        v-if="exchangeRateIndexShown"
        :exchange-rates.sync="exchangeRates"
        />
    </v-form>
</template>

<script>
import PurchaseOrderFormLine from './PurchaseOrderFormLine.vue';
import PDatePicker from './../common/PDatePicker'
import { randomSix } from '../../utils/toolkit';
import { mapActions, mapGetters } from 'vuex';
import { getTotal } from './../../utils/accounting';
import { formatNumber } from 'accounting';
import { isRequired, isNumeric } from './../../utils/validations';
import ExchangeRateIndex from './../accounting/ExchangeRateIndex.vue';
import {searchExchangeRates} from './../../services/ledger/exchangeRates';

export default {
    name: "PurchaseOrderForm",
    components: {
        PurchaseOrderFormLine,
        PDatePicker,
        ExchangeRateIndex
    },
    props: {
        value: Object,
        exchangeRateIndexShown: Boolean,
    },
    data() {
        return {
            form: {
                id: null,
                code: null,
                date: null,
                warehouse_id: null,
                receiving_point: null,
                requisition_id: null,
                supplier_id: null,
                currency_id: null,
                amount: null,
                base_amount: null,
                lines: [],
            },
            line: { 
                item_id: null,
                quantity: null,
                measure_unit_id: null, 
                price: null, 
                tax_id: null,
                subtotal: 0,
                tax: 0,
                total: 0
            },
            exchangeRates: []
        }
    },
    watch: {
        form: {
            deep: true,
            handler(form) {
                this.$emit("input", {...form, lines: [...form.lines]})
            }
        },
        "form.date": {
            handler(date) {
                if(!this.editMode) {
                    this.fetchExchangeRates(date)
                }
            }
        },
        amount(value) {
            this.form.amount = value;
        },
        baseAmount(value) {
            this.form.base_amount = value;
        }
    },
    computed: {
        ...mapGetters({
            suppliers: "purchase/supplier/list",
            currencies: "purchase/currency/list",
            warehouses: "purchase/warehouse/list",
        }),
        editMode() {
            return this.form.id ? true : false;
        },
        currency() {
            return this.currencies.find(currency => currency.id == this.form.currency_id);
        },
        amount() {
            return getTotal(this.form.lines, "total");
        },
        baseAmount() {
            return this.amount * this.exchangeRate;
        },
        exchangeRate() {
            if(!this.currency) return 0;
            let rate = this.exchangeRates.find(each => each.currency_id == this.currency.id);
            if (!rate) return 0;
            return rate.customRate;
        }
    },
    methods: {
        isRequired, isNumeric, getTotal,
        ...mapActions({
            searchCurrency: "purchase/currency/search",
            searchSuppliers: "purchase/supplier/search",
            searchWarehouses: "purchase/warehouse/search",
            TaxRatesSearch: "purchase/tax/index",
            snackbar: "ui/snackbar/show",
        }),
        formatNumber(n) {
            return formatNumber(n, 2);
        },
        addLine() {
            this.form.lines.push({ ...this.line, key: randomSix() })
        },
        handleDeleteLine(index) {
            this.form.lines.splice(index, 1);
        },
        resetLines(){
            this.form.lines = []
        },
        validate() {
            return this.$refs.form.validate() && this.amount > 0 && this.baseAmount > 0;
        },
        fill() {
            let form = {...this.form, ...this.value};
            if(this.value.lines && Array.isArray(this.value.lines)) {
                form = {...form, lines: [...form.lines]}
            }
            this.form = form;
        },
        async fetchExchangeRates(date) {
            let response = await searchExchangeRates({date});
            this.exchangeRates = response.data.rates.map(each => {
                return {
                    ...each,
                    customRate: each.rate
                }
            });
        },
        /**
         * evaluateCustomRates fetchs the custom rate from api
         * and override the custom rate 
         */
        evaluateCustomRates() {
            
            var originalRates = [];

            originalRates.push({
                currency: this.form.currency,
                rate: this.form.exchange_rate
            });

            (async function() {
                await this.fetchExchangeRates(this.form.date)

                originalRates.forEach(each => {
                    let i = this.exchangeRates.findIndex(exchangeRate => exchangeRate.currency_id == each.currency.id)
                    this.exchangeRates[i].customRate = each.rate;
                })                
            }).bind(this)()
        },
    },
    mounted() {
        if(this.value) this.fill();
        this.TaxRatesSearch();
        this.evaluateCustomRates();
    }
};
</script>

<style></style>
